import { ProFormCascader } from "@ant-design/pro-components";
import { useIntl } from "@umijs/max";
import React, { useEffect, useState } from "react";
import { listOperatorAgentTree, type AgentTreeItem } from "@/services/common";

export interface AgentCascaderProps {
  /** 选中的层级限制: 例如传 3 则最多选到二级代理 */
  level?: number;
  /** 是否显示顶层平台 (pid: 0) */
  showPlatform?: boolean;
  /** onChange 接收当前选择的节点 ID (如果是多选，则返回最后一个选中的节点数组，或者根据需要调整) */
  onChange?: (value: any | undefined) => void;
  /** 当路径改变时触发，返回完整的节点对象数组 */
  onPathChange?: (selectedOptions: any[]) => void;
  /** ProFormCascader 的其他属性 */
  [key: string]: any;
}

/**
 * 递归寻找路径上的所有节点
 */
const findPathById = (
  targetId: number,
  currentOptions: any[],
  path: any[] = [],
): any[] | null => {
  for (const opt of currentOptions) {
    if (opt.value === targetId) {
      return [...path, opt];
    }
    if (opt.children) {
      const found = findPathById(targetId, opt.children, [...path, opt]);
      if (found) return found;
    }
  }
  return null;
};

/**
 * 格式化树数据为 Cascader 要求的格式
 */
const formatOptions = (
  data: AgentTreeItem[],
  levelLimit?: number,
  currentLevel: number = 2,
): any[] => {
  if (!data || !Array.isArray(data)) return [];

  return data.map((item) => {
    const node: any = {
      label: item.operatorName,
      value: item.id,
      level: currentLevel,
      fee: item.fee,
    };

    if (item.children && item.children.length > 0) {
      // 如果没有限制层级，或者当前层级还没达到限制，则递归处理子节点
      if (!levelLimit || currentLevel < levelLimit) {
        node.children = formatOptions(
          item.children,
          levelLimit,
          currentLevel + 1,
        );
      }
    }

    return node;
  });
};

/**
 * 自定义 Hook 处理代理树数据获取与格式化
 */
const useAgentTree = (options: {
  levelLimit?: number;
  showPlatform?: boolean;
  intl: ReturnType<typeof useIntl>;
}) => {
  const { levelLimit, showPlatform, intl } = options;
  const [treeOptions, setTreeOptions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchTree = async () => {
    setLoading(true);
    try {
      const res = await listOperatorAgentTree();
      const data = Array.isArray(res) ? res : (res as any)?.list || [];
      let formattedOptions = formatOptions(data, levelLimit);

      if (showPlatform) {
        formattedOptions = [
          {
            label: intl.formatMessage({ id: "pages.common.platform" }),
            value: 0,
            level: 1,
            children: formattedOptions,
          },
        ];
      }
      setTreeOptions(formattedOptions);
    } catch (error) {
      console.error("Failed to fetch agent tree:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTree();
  }, [levelLimit]);

  return { options: treeOptions, loading };
};

/**
 * 封装公共的代理选择器 (级联选择)
 * 适配 @api/v1/sys/listOperatorAgentTree
 */
const AgentCascader: React.FC<AgentCascaderProps> = (props) => {
  const intl = useIntl();
  const { level, onChange, onPathChange, fieldProps, ...rest } = props;
  const { options, loading } = useAgentTree({
    levelLimit: level,
    showPlatform: props.showPlatform,
    intl,
  });
  const [hasInitialized, setHasInitialized] = useState(false);

  useEffect(() => {
    // 仅在初始加载 tree 数据完成，且未进行初始化触发时执行一次
    if (
      !hasInitialized &&
      onPathChange &&
      rest.initialValue !== undefined &&
      options.length > 0
    ) {
      const targetId = Array.isArray(rest.initialValue)
        ? rest.initialValue[rest.initialValue.length - 1]
        : rest.initialValue;

      if (targetId !== undefined && targetId !== null) {
        const path = findPathById(targetId, options);
        if (path) {
          onPathChange(path);
          setHasInitialized(true);
        }
      }
    }
  }, [rest.initialValue, options, hasInitialized, onPathChange]);

  const handleCascaderChange = React.useCallback(
    (value: any[], selectedOptions: any[]) => {
      setHasInitialized(true);
      if (fieldProps?.onChange) {
        fieldProps.onChange(value, selectedOptions);
      }
      if (onChange) {
        if (fieldProps?.multiple) {
          const lastIds = value?.map((path: any[]) =>
            path && path.length > 0 ? path[path.length - 1] : undefined,
          );
          onChange(lastIds);
        } else {
          const lastId =
            value && value.length > 0 ? value[value.length - 1] : undefined;
          onChange(lastId);
        }
      }
      if (onPathChange) {
        onPathChange(selectedOptions || []);
      }
    },
    [fieldProps, onChange, onPathChange],
  );

  const cascaderFieldProps = React.useMemo(
    () => ({
      options,
      loading,
      placeholder:
        props.placeholder ||
        intl.formatMessage({ id: "pages.common.agent.placeholder" }),
      changeOnSelect: true,
      expandTrigger: "hover" as const,
      fieldNames: { label: "label", value: "value", children: "children" },
      ...fieldProps,
      onChange: handleCascaderChange,
    }),
    [
      options,
      loading,
      props.placeholder,
      intl,
      fieldProps,
      handleCascaderChange,
    ],
  );

  return <ProFormCascader {...rest} fieldProps={cascaderFieldProps} />;
};

export default AgentCascader;
